import {
  createAssetSchema,
  depreciateSchema,
  disposeAssetSchema,
  PAYMENT_METHODS,
} from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';
import { withCurrency, type Asset, type Message, type Supplier } from './shared';

interface Summary {
  assets: number;
  costMinor: number;
  accumulatedMinor: number;
  netBookValueMinor: number;
  currency: string;
}

const blankAsset = {
  name: '',
  category: '',
  supplierId: '',
  date: '',
  cost: '',
  salvage: '',
  life: '',
};
const blankDispose = { proceeds: '', method: 'cash', note: '' };

/** The month before the current one, as YYYY-MM: the usual month to depreciate. */
function lastMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
}

export function AssetsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(blankAsset);
  const [period, setPeriod] = useState(lastMonth);
  const [disposing, setDisposing] = useState('');
  const [dispose, setDispose] = useState(blankDispose);
  const [page, setPage] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Message>(null);

  const assets = useQuery({
    queryKey: ['inventory', schoolId, 'assets', page],
    queryFn: () =>
      apiList<Asset>(`/schools/${schoolId}/inventory/assets`, {
        query: { page, pageSize: 20 },
      }).then(withCurrency),
  });
  const summary = useQuery({
    queryKey: ['inventory', schoolId, 'assets', 'summary'],
    queryFn: () => api<Summary>(`/schools/${schoolId}/inventory/assets/summary`),
  });
  const suppliers = useQuery({
    queryKey: ['inventory', schoolId, 'suppliers', 'all'],
    queryFn: () =>
      apiList<Supplier>(`/schools/${schoolId}/inventory/suppliers`, {
        query: { status: 'active', pageSize: 100 },
      }).then((r) => r.data),
    enabled: can('inventory.assets', schoolId),
  });
  const currency = assets.data?.currency ?? 'USD';

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['inventory', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const register = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/inventory/assets`, { method: 'POST', body }),
    onSuccess: () => {
      setForm(blankAsset);
      setMessage({ kind: 'ok', text: t('inv.assetAdded') });
      refresh();
    },
    onError: fail,
  });
  const depreciate = useMutation({
    mutationFn: () =>
      api<{ assetsDepreciated: number; totalMinor: number; alreadyDone: number; currency: string }>(
        `/schools/${schoolId}/inventory/assets/depreciation`,
        { method: 'POST', body: { period } },
      ),
    onSuccess: (r) => {
      setMessage({
        kind: 'ok',
        text: t('inv.depreciated', {
          count: r.assetsDepreciated,
          amount: formatMinor(r.totalMinor, r.currency),
          already: r.alreadyDone,
        }),
      });
      refresh();
    },
    onError: fail,
  });
  const retire = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/inventory/assets/${disposing}/dispose`, { method: 'POST', body }),
    onSuccess: () => {
      setDisposing('');
      setDispose(blankDispose);
      setMessage({ kind: 'ok', text: t('inv.disposed') });
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('inv.tab.assets')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {summary.data && (
        <p className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
          {t('inv.assetSummary', {
            count: summary.data.assets,
            cost: formatMinor(summary.data.costMinor, summary.data.currency),
            depreciation: formatMinor(summary.data.accumulatedMinor, summary.data.currency),
            nbv: formatMinor(summary.data.netBookValueMinor, summary.data.currency),
          })}
        </p>
      )}
      {assets.data?.data.length === 0 && <p className="text-slate-600">{t('inv.noAssets')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {assets.data?.data.map((a) => (
          <li key={a.id} className="space-y-2 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <span className="font-mono text-xs">{a.tag}</span>{' '}
                <span className="font-medium">{a.name}</span>
                {a.category ? ` · ${a.category}` : ''}
                {a.location ? ` · ${a.location}` : ''}
                {a.custodianName ? ` · ${a.custodianName}` : ''} ·{' '}
                {t(`inv.assetStatus.${a.status}` as MessageKey)}
              </span>
              <span className="flex items-center gap-3">
                <span>
                  {t('inv.nbv', {
                    nbv: formatMinor(a.netBookValueMinor, currency),
                    cost: formatMinor(a.costMinor, currency),
                  })}
                </span>
                {can('inventory.dispose', schoolId) && a.status !== 'disposed' && (
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('inv.dispose')}: ${a.name}`}
                    onClick={() => setDisposing(disposing === a.id ? '' : a.id)}
                  >
                    {t('inv.dispose')}
                  </button>
                )}
              </span>
            </div>
            {disposing === a.id && (
              <form
                noValidate
                aria-label={t('inv.disposeForm')}
                className="grid gap-3 rounded bg-slate-50 p-3 sm:grid-cols-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const proceeds =
                    dispose.proceeds.trim() === '' ? 0 : parseMajor(dispose.proceeds, currency);
                  const parsed = disposeAssetSchema.safeParse({
                    proceedsMinor: proceeds,
                    method: proceeds ? dispose.method : undefined,
                    note: dispose.note,
                  });
                  const next: Record<string, string> = {};
                  if (!parsed.success)
                    for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
                  if (proceeds === null) next.proceedsMinor = t('inv.invalid');
                  setErrors(next);
                  if (!parsed.success || Object.keys(next).length > 0) return;
                  setMessage(null);
                  retire.mutate(parsed.data);
                }}
              >
                <TextField
                  label={t('inv.proceeds', { currency })}
                  inputMode="decimal"
                  value={dispose.proceeds}
                  error={errors.proceedsMinor}
                  onChange={(e) => setDispose({ ...dispose, proceeds: e.target.value })}
                />
                <div>
                  <label htmlFor="dispose-method" className="block text-sm font-medium">
                    {t('inv.method')}
                  </label>
                  <select
                    id="dispose-method"
                    className={selectClass}
                    value={dispose.method}
                    onChange={(e) => setDispose({ ...dispose, method: e.target.value })}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {t(`fees.method.${m}` as MessageKey)}
                      </option>
                    ))}
                  </select>
                </div>
                <TextField
                  label={t('inv.disposeNote')}
                  value={dispose.note}
                  error={errors.note}
                  onChange={(e) => setDispose({ ...dispose, note: e.target.value })}
                />
                <div className="flex items-end">
                  <button type="submit" className={primaryButton} disabled={retire.isPending}>
                    {t('inv.disposeNow')}
                  </button>
                </div>
              </form>
            )}
          </li>
        ))}
      </ul>
      {assets.data && assets.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={assets.data.meta.totalPages} onPage={setPage} />
      )}

      {can('inventory.assets', schoolId) && (
        <div className="flex flex-wrap items-end gap-4">
          <TextField
            type="month"
            label={t('inv.month')}
            hint={t('inv.monthHint')}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
          <button
            type="button"
            className={primaryButton}
            disabled={!depreciateSchema.safeParse({ period }).success || depreciate.isPending}
            onClick={() => {
              setMessage(null);
              depreciate.mutate();
            }}
          >
            {t('inv.runDepreciation')}
          </button>
        </div>
      )}

      {can('inventory.assets', schoolId) && (
        <form
          noValidate
          aria-label={t('inv.addAsset')}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const cost = parseMajor(form.cost, currency);
            const salvage = form.salvage.trim() === '' ? 0 : parseMajor(form.salvage, currency);
            const parsed = createAssetSchema.safeParse({
              name: form.name,
              category: form.category,
              supplierId: form.supplierId,
              purchaseDate: form.date,
              costMinor: cost,
              salvageMinor: salvage,
              usefulLifeMonths: Number(form.life),
            });
            const next: Record<string, string> = {};
            if (!parsed.success)
              for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
            if (cost === null) next.costMinor = t('inv.invalid');
            if (salvage === null) next.salvageMinor = t('inv.invalid');
            setErrors(next);
            if (!parsed.success || Object.keys(next).length > 0) return;
            setMessage(null);
            register.mutate(parsed.data);
          }}
        >
          <TextField
            label={t('inv.assetName')}
            value={form.name}
            error={errors.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label={t('inv.category')}
            value={form.category}
            error={errors.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
          <div>
            <label htmlFor="asset-supplier" className="block text-sm font-medium">
              {t('inv.supplier')}
            </label>
            <select
              id="asset-supplier"
              className={selectClass}
              value={form.supplierId}
              onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
            >
              <option value="">{t('inv.choose')}</option>
              {suppliers.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {errors.supplierId && (
              <p role="alert" className="mt-1 text-xs text-red-700">
                {t('inv.chooseOne')}
              </p>
            )}
          </div>
          <TextField
            type="date"
            label={t('inv.purchaseDate')}
            value={form.date}
            error={errors.purchaseDate}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
          <TextField
            label={t('inv.cost', { currency })}
            inputMode="decimal"
            value={form.cost}
            error={errors.costMinor}
            onChange={(e) => setForm({ ...form, cost: e.target.value })}
          />
          <TextField
            label={t('inv.salvage', { currency })}
            inputMode="decimal"
            value={form.salvage}
            error={errors.salvageMinor}
            onChange={(e) => setForm({ ...form, salvage: e.target.value })}
          />
          <TextField
            label={t('inv.lifeMonths')}
            inputMode="numeric"
            value={form.life}
            error={errors.usefulLifeMonths}
            onChange={(e) => setForm({ ...form, life: e.target.value })}
          />
          <div className="flex items-end">
            <button type="submit" className={primaryButton} disabled={register.isPending}>
              {t('inv.addAsset')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
